/**
 * The outgoing ring, played from the operator's own file.
 *
 * ALWAYS A FILE, never oscillators, and that is the difference from the
 * incoming ring rather than an oversight. This sound only exists when somebody
 * uploaded one - there is no synthesised default to fall back to - so the
 * branch the incoming ring needs (`hasCustomRing() || isDesktopApp()`) has
 * nothing to decide here.
 *
 * IT LOOPS END TO END rather than restarting on a cadence. The incoming ring
 * plays one second of tone every three seconds because that is what a desk
 * phone does and the shipped asset is exactly that one second. An outgoing ring
 * is whatever the operator uploaded - a tune, a tone pattern, a jingle with its
 * own gaps in it - and chopping it every three seconds would play the first
 * three seconds of their file over and over. Looping means the file they heard
 * on the settings page is the file they hear on the phone.
 *
 * No user gesture is needed and none is missing: an outgoing call starts with
 * the operator pressing dial, so the autoplay policy has already been satisfied
 * by the click that placed the call. A refusal is still handled, because a
 * browser is allowed to refuse for other reasons and a silent wait is a far
 * better failure than an unhandled rejection.
 */
import { readAudioVolume } from "../audio/volume";
import { fileRingVolume, type Ring } from "../incoming/ringSpec";

/** `setSinkId` is not in every lib.dom this repo compiles against. */
type SinkCapableElement = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/**
 * How often the volume is re-read while the ring is playing.
 *
 * The same reasoning as the incoming ring's per-burst read: a call rings for as
 * long as somebody takes to pick up, which is exactly long enough to reach for
 * the slider - and a slider that only takes effect on the NEXT call is a slider
 * that does nothing at the one moment anybody uses it. There is no burst here
 * to hang it on, so it is a plain interval.
 */
const VOLUME_POLL_MS = 250;

/**
 * Start the outgoing ring from `url`, on `deviceId` when one is chosen.
 *
 * Returns null when the element refuses to play. The caller has nothing to fall
 * back to and should not invent one: a quiet wait is what an operator got
 * before this feature existed.
 */
export async function startRingback(url: string, deviceId: string): Promise<Ring | null> {
  if (typeof document === "undefined" || !url) return null;

  const el = document.createElement("audio") as SinkCapableElement;
  el.src = url;
  el.loop = true;
  el.preload = "auto";
  el.setAttribute("aria-hidden", "true");
  el.style.display = "none";
  document.body.appendChild(el);

  const drop = () => {
    el.pause();
    el.remove();
  };

  const applyVolume = () => {
    el.volume = fileRingVolume(readAudioVolume().ring);
  };

  // THE CALL SPEAKER, not the ring speaker, and the two are genuinely different
  // choices in `audio/choice.ts`. An operator puts the incoming ring on the desk
  // speakers so they can hear the office phone from across the room; this sound
  // arrives after they have already pressed dial and are holding the headset,
  // and it is the last thing they hear before a voice comes out of that same
  // headset. It is also where the SDK's own outgoing sound went - Twilio routes
  // `[voice, outgoing, disconnect, dtmf]` through `speakerDevices` - so this
  // keeps the swap faithful rather than moving the sound to another room.
  //
  // The LEVEL still comes from the ring slider (`fileRingVolume` above): this is
  // a ring, the incoming one already settled what a ring in this CRM sounds
  // like, and two rings at wildly different loudness would be the real surprise.
  if (deviceId && typeof el.setSinkId === "function") {
    // The wrong speaker beats no speaker, so a refused sink is not fatal.
    await el.setSinkId(deviceId).catch(() => {});
  }

  applyVolume();
  try {
    await el.play();
  } catch {
    drop();
    return null;
  }

  const timer = window.setInterval(applyVolume, VOLUME_POLL_MS);

  return {
    stop: () => {
      window.clearInterval(timer);
      drop();
    },
  };
}
